import React, { useEffect, useState } from 'react';
import { Download, Package, ExternalLink, Lock } from 'lucide-react';
import api from '../../utils/api';

const UserProducts = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPurchasedProducts();
  }, []);

  const fetchPurchasedProducts = async () => {
    try {
      const response = await api.get('/orders');
      const allOrders = response.data;
      setOrders(allOrders);
      
      // Extract all products from approved orders only
      const allProducts = [];
      for (const order of allOrders) {
        if (order.admin_approved && order.payment_status === 'completed') {
          for (const item of order.items) {
            if (item.product_type === 'product') {
              // Fetch full product details
              try {
                const productResponse = await api.get(`/products/${item.product_id}`);
                allProducts.push({
                  ...item,
                  fullDetails: productResponse.data,
                  orderId: order.id
                });
              } catch (error) {
                console.error('Failed to fetch product details:', error);
                allProducts.push({...item, orderId: order.id});
              }
            }
          }
        }
      }
      setProducts(allProducts);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="user-products-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-8" data-testid="products-title">My Products</h1>

        {products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product, index) => (
              <div key={index} className="glass-card p-6" data-testid={`product-${index}`}>
                <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Order #{product.orderId?.slice(0, 8)}
                </p>
                
                {/* Files Section */}
                {product.fullDetails?.files && product.fullDetails.files.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-sm font-semibold mb-2">Download Files:</p>
                    {product.fullDetails.files.map((file, idx) => (
                      <a
                        key={idx}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full px-4 py-2 bg-primary/20 text-primary rounded-lg font-semibold hover:bg-primary/30 transition-all flex items-center justify-center gap-2"
                        data-testid={`download-file-${idx}`}
                      >
                        <Download className="w-4 h-4" />
                        {file.name}
                      </a>
                    ))}
                  </div>
                )}
                
                {/* External Links Section */}
                {product.fullDetails?.external_links && product.fullDetails.external_links.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-sm font-semibold mb-2">Access Links:</p>
                    {product.fullDetails.external_links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full px-4 py-2 bg-secondary/20 text-secondary rounded-lg font-semibold hover:bg-secondary/30 transition-all flex items-center justify-center gap-2"
                        data-testid={`access-link-${idx}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                        {link.name}
                      </a>
                    ))}
                  </div>
                )}
                
                {(!product.fullDetails?.files?.length && !product.fullDetails?.external_links?.length) && (
                  <div className="w-full px-4 py-3 bg-accent/10 text-accent rounded-lg text-center text-sm">
                    Contact admin for product access
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 glass-card" data-testid="no-products">
            <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-4">No approved products yet</h2>
            <p className="text-muted-foreground">Purchase products and wait for admin approval to access them</p>
          </div>
        )}
        
        {/* Pending Orders Notice */}
        {orders.filter(o => !o.admin_approved && o.payment_status !== 'rejected').length > 0 && (
          <div className="mt-8 glass-card p-6 border-l-4 border-orange-500">
            <h3 className="text-lg font-semibold mb-2 text-orange-400">Orders Pending Approval</h3>
            <p className="text-muted-foreground">
              You have {orders.filter(o => !o.admin_approved && o.payment_status !== 'rejected').length} order(s) 
              waiting for admin approval. Products will be accessible once approved.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProducts;
